import { AlertCircle, Calendar, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

const TIME_SLOTS = [
    '09:00', '10:00', '11:00', '12:00',
    '14:00', '15:00', '16:00'
];

export const RescheduleAppointment = () => {
    const { id: appointmentId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { get, put } = useApi();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [appointment, setAppointment] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    const [formData, setFormData] = useState({
        newDate: '',
        newTimeSlot: '',
        reason: ''
    });

    // Fetch appointment details
    useEffect(() => {
        const fetchAppointment = async () => {
            try {
                setLoading(true);
                const response = await get(`/appointments/${appointmentId}`);
                setAppointment(response.data.data);
                setError(null);
            } catch (err) {
                setError('Failed to load appointment details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAppointment();
    }, [appointmentId, get]);

    // Fetch available slots when date changes
    useEffect(() => {
        const fetchAvailableSlots = async () => {
            if (!formData.newDate || !appointment) return;

            try {
                setLoadingSlots(true);
                const response = await get(
                    `/appointments/available-slots?counselorId=${appointment.counselor._id}&date=${formData.newDate}`
                );
                setAvailableSlots(response.data.data || []);
            } catch (err) {
                console.error('Failed to fetch available slots:', err);
                setAvailableSlots([]);
            } finally {
                setLoadingSlots(false);
            }
        };

        fetchAvailableSlots();
    }, [formData.newDate, appointment, get]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.newDate || !formData.newTimeSlot) {
            setError('Please select both date and time slot');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            await put(`/appointments/${appointmentId}`, {
                date: formData.newDate,
                timeSlot: formData.newTimeSlot,
                notes: formData.reason ? `Rescheduled: ${formData.reason}` : appointment.notes
            });

            setSuccess(true);

            // Redirect after success
            setTimeout(() => {
                navigate(`/${user.role}/appointments`);
            }, 2000);

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reschedule appointment');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    // Get minimum date (tomorrow)
    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-uzima-maroon-500"></div>
            </div>
        );
    }

    if (!appointment) {
        return (
            <div className="max-w-2xl mx-auto p-6">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p className="ml-3 text-sm text-red-700">Appointment not found</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={handleCancel}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Reschedule Appointment</h1>
                <p className="text-gray-600 mt-1">Select a new date and time for your appointment</p>
            </div>

            {/* Success Message */}
            {success && (
                <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                        <div className="ml-3">
                            <p className="text-sm text-green-700 font-medium">Appointment rescheduled successfully!</p>
                            <p className="text-sm text-green-600 mt-1">Redirecting...</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p className="ml-3 text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Appointment Details */}
                <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-gray-600" />
                        Current Appointment
                    </h2>
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm text-gray-600">Counselor</p>
                            <p className="font-medium text-gray-900">{appointment.counselor?.fullName}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Date</p>
                            <p className="font-medium text-gray-900">
                                {new Date(appointment.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Time</p>
                            <p className="font-medium text-gray-900">{appointment.timeSlot}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Service Type</p>
                            <p className="font-medium text-gray-900">{appointment.serviceType}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Session Type</p>
                            <p className="font-medium text-gray-900 capitalize">{appointment.sessionType}</p>
                        </div>
                    </div>
                </div>

                {/* Reschedule Form */}
                <div className="bg-white rounded-lg p-6 border-2 border-blue-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Clock className="h-5 w-5 mr-2 text-blue-600" />
                        New Appointment Time
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Date Picker */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select New Date
                            </label>
                            <input
                                type="date"
                                min={getMinDate()}
                                value={formData.newDate}
                                onChange={(e) => setFormData({ ...formData, newDate: e.target.value, newTimeSlot: '' })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                You can reschedule to any date from tomorrow onwards
                            </p>
                        </div>

                        {/* Time Slot Selector */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Time Slot
                            </label>
                            {!formData.newDate ? (
                                <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-md">
                                    Please select a date first
                                </p>
                            ) : loadingSlots ? (
                                <div className="py-4 text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                    <p className="text-sm text-gray-500 mt-2">Loading available slots...</p>
                                </div>
                            ) : availableSlots.length === 0 ? (
                                <p className="text-sm text-amber-600 py-4 text-center bg-amber-50 rounded-md">
                                    No available slots for this date. Please select another date.
                                </p>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    {availableSlots.map((slot) => (
                                        <button
                                            key={slot}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, newTimeSlot: slot })}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${formData.newTimeSlot === slot
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            {slot}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Reason (Optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason for Rescheduling (Optional)
                            </label>
                            <textarea
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Let us know why you need to reschedule..."
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !formData.newDate || !formData.newTimeSlot}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-blue-300 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <>
                                        <span className="inline-block animate-spin mr-2">⌛</span>
                                        Rescheduling...
                                    </>
                                ) : (
                                    'Confirm Reschedule'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Warning Notice */}
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <div className="flex">
                    <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                        <p className="text-sm text-blue-700">
                            <strong>Please note:</strong> Your counselor will be notified of this change.
                            If you need to cancel instead of reschedule, please use the cancel option from the appointment details.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RescheduleAppointment;
